// ============================================================
// SquadBar - one click between the units of your control network.
//
// Movement has never needed a mode switch (`BattlefieldPanel` moves whoever
// you drag), but everything else does: to answer for a servitor you must
// first find them in the roster picker. For a player running four hounds that
// is the whole friction of the evening.
//
// NOT a second "acting as". It sets the same `actorId` the picker sets, and
// nothing about the network gates who may do it - see
// `engine/roster/control.ts`.
// ============================================================
import { networkOfActor } from '../engine/roster/control';
import type { Combatant } from '../engine/roster/types';
import clsx from '../utils/clsx';
import { useSeesHidden } from '../hooks/useSeesHidden';
import styles from '../styles/SquadBar.module.css';

interface Props {
  roster: Combatant[];
  actorId: string | null;
  onSelect: (id: string) => void;
  /** Who owes a reaction now (the reaction queue): their chip pulses red until it is spent (plan map-display, E). */
  owedIds?: readonly string[];
  /** Stacked under the actor's rail (plan design-gaps, L7): the chips in a column, the orphan note as a mark. */
  compact?: boolean;
  /** The bar's id for the specs: the panel's and the rail's stay apart, both mounted. */
  testId?: string;
}

export default function SquadBar({
  roster,
  actorId,
  onSelect,
  owedIds = [],
  compact = false,
  testId = 'squad-bar',
}: Props) {
  // Only a screen that may see hidden figures is told a controller is missing (2026-10-22, `useSeesHidden`).
  const seesHidden = useSeesHidden();
  const network = networkOfActor(roster, actorId);
  if (!network) return null;

  return (
    <div className={clsx(styles.bar, compact && styles.compact)} data-testid={testId} data-tag={network.tag}>
      <span className={styles.label} title={network.tag}>
        {network.label}
      </span>

      {network.members.map((member) => (
        <button
          key={member.id}
          className={clsx(
            styles.unit,
            member.id === actorId && styles.unitActive,
            owedIds.includes(member.id) && styles.unitOwed,
          )}
          data-testid={`squad-unit-${member.id}`}
          data-owed={owedIds.includes(member.id) || undefined}
          onClick={() => onSelect(member.id)}
          aria-pressed={member.id === actorId}
          title={member.id === network.controller?.id ? `${member.name} - controls this network` : member.name}
        >
          {member.id === network.controller?.id && <span aria-hidden="true">★ </span>}
          {member.name}
        </button>
      ))}

      {/* THE SILENT FAILURE, SAID OUT LOUD. A network whose controller is not
          in this session raises nobody's alarm, and looks exactly like a
          network that works. Everyone still sees every reaction either way -
          this only explains why nothing is shouting. Not to a player: a
          hidden controller looks absent to them (`useSeesHidden`). */}
      {!network.controller && seesHidden && (
        <span className={styles.orphan} data-testid="squad-orphan">
          no controller here - nobody is alarmed for this squad
        </span>
      )}
    </div>
  );
}
